import React, { FC, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { buttonVariants } from '@clatter/ui';
import { NgButton, DropdownSelect } from '../../UI';
import { ClassificationOption } from '../../../interfaces';
import { viewStateSelectors } from '../../../store';
import { PolledThumbnailImage } from './SlideRow';

export interface SlideClassification {
  option: ClassificationOption;
  replacesSlideId?: string;
}

interface ClassifyDialogProps {
  open: boolean;
  slideTitle?: string;
  thumbnailUrl?: string;
  originalSlides?: FolderManagerItem[];
  excludedOriginalSlideIds?: Set<string>;
  initialOption?: ClassificationOption;
  initialReplacesSlideId?: string;
  viewStateKey?: string;
  onClose: () => void;
  onSave?: (option: ClassificationOption, replacesSlideId?: string) => void;
}

const StyledDialogPaper = styled.div`
  width: 640px;
  max-width: 90vw;
`;

const StyledHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.default};
  border-bottom: 1px solid ${({ theme }) => theme.palette.cardBorder};
`;

const StyledHeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StyledTitle = styled.h2`
  margin: 0;
  font-weight: bold;
  font-size: ${({ theme }) => theme.fontSize.h4};
  color: ${({ theme }) => theme.palette.text};
`;

const StyledSubtitle = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.palette.textDimmed};
`;

const StyledContent = styled.div`
  padding: ${({ theme }) => theme.spacing.default};
`;

const StyledPreviewRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.default};
  margin-bottom: ${({ theme }) => theme.spacing.default};
`;

const StyledPreviewColumn = styled.div`
  flex: 1;
  min-width: 0;
`;

const StyledSectionLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 700;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.palette.textDimmed};
  margin-bottom: ${({ theme }) => theme.spacing.small};
`;

const StyledPreviewBox = styled.div<{ $tint?: string; $compact?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${({ $compact }) => ($compact ? '160px' : '280px')};
  border-radius: ${({ theme }) => theme.radius.default};
  background-color: ${({ $tint }) => $tint || '#eef1fb'};
  overflow: hidden;
`;

const StyledArrow = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.palette.textDimmed};
`;

const StyledOptionCard = styled.div<{ $selected: boolean }>`
  border: 1px solid ${({ theme, $selected }) => ($selected ? theme.palette.primaryButton : theme.palette.cardBorder)};
  background-color: ${({ $selected }) => ($selected ? 'rgba(33,150,243,0.06)' : 'transparent')};
  border-radius: ${({ theme }) => theme.radius.default};
  margin-bottom: ${({ theme }) => theme.spacing.small};
`;

const StyledOption = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: ${({ theme }) => theme.spacing.small} ${({ theme }) => theme.spacing.default} 4px;
  cursor: pointer;
`;

const StyledReplacesSection = styled.div`
  padding: 0 ${({ theme }) => theme.spacing.default} ${({ theme }) => theme.spacing.small};

  > span {
    display: block;
    margin-bottom: 4px;
  }
`;

const StyledOptionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
`;

const StyledOptionTitle = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.palette.text};
`;

const StyledOptionDescription = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.palette.textDimmed};
`;

// Stable reference: a fresh object here would make react-select treat the menu as
// "changed" on every render and reset its hover/focus index back to the first option.
const REPLACES_SELECT_STYLES = {
  menuPortal: (base: any) => ({ ...base, zIndex: 1400 }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isFocused ? '#e3f2fd' : 'white',
    color: '#1a1a1a',
    cursor: 'pointer',
  }),
};

const StyledFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: ${({ theme }) => theme.spacing.default};
  border-top: 1px solid ${({ theme }) => theme.palette.cardBorder};
`;

const EMPTY_EXCLUDED_IDS = new Set<string>();

export const ClassifyDialog: FC<ClassifyDialogProps> = ({
  open,
  slideTitle,
  thumbnailUrl,
  originalSlides = [],
  excludedOriginalSlideIds = EMPTY_EXCLUDED_IDS,
  initialOption = ClassificationOption.NewSlide,
  initialReplacesSlideId,
  viewStateKey,
  onClose,
  onSave,
}) => {
  const [selectedOption, setSelectedOption] = useState<ClassificationOption>(initialOption);
  const selectedThemeId = useSelector(viewStateSelectors.getSelectedThemeId(viewStateKey || ''));

  // slides already claimed by other resulting slides are unavailable, except this slide's own
  // current pick (grandfathered in so an already-saved selection doesn't vanish from the list)
  const replacesOptions = useMemo(
    () =>
      originalSlides
        .map((slide, index) => ({
          value: String(slide.id),
          label: `${String(index + 1).padStart(2, '0')} - ${slide.displayName || slide.name}`,
        })),
        // .filter((option) => option.value === initialReplacesSlideId || !excludedOriginalSlideIds.has(option.value)),
    [originalSlides, excludedOriginalSlideIds, initialReplacesSlideId],
  );

  const [replacesSlideId, setReplacesSlideId] = useState<string | undefined>(
    initialReplacesSlideId ?? (replacesOptions[0] ? replacesOptions[0].value : undefined),
  );

  const replacingSlide = originalSlides.find((slide) => String(slide.id) === replacesSlideId);

  const hasReplacesSlide =
    selectedOption === ClassificationOption.VersionUpdate || selectedOption === ClassificationOption.Duplicate;

  const handleSave = () => {
    onSave?.(selectedOption, hasReplacesSlide ? replacesSlideId : undefined);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false}>
      <StyledDialogPaper>
        <StyledHeader>
          <StyledHeaderLeft>
            <StyledTitle>{slideTitle || 'Slide'}</StyledTitle>
            <StyledSubtitle>Classify this slide</StyledSubtitle>
          </StyledHeaderLeft>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </StyledHeader>

        <StyledContent>
          <StyledPreviewRow>
            <StyledPreviewColumn>
              <StyledSectionLabel>NEW SLIDE</StyledSectionLabel>
              <StyledPreviewBox $compact={hasReplacesSlide}>
                {thumbnailUrl && <PolledThumbnailImage thumbnailUrl={thumbnailUrl} themeId={selectedThemeId} />}
              </StyledPreviewBox>
            </StyledPreviewColumn>

            {hasReplacesSlide && (
              <>
                <StyledArrow>
                  <ArrowForwardIcon />
                </StyledArrow>

                <StyledPreviewColumn>
                  <StyledSectionLabel>
                    {selectedOption === ClassificationOption.Duplicate ? 'DUPLICATE OF' : 'REPLACING'}
                  </StyledSectionLabel>
                  <StyledPreviewBox $tint="#f3e9d8" $compact>
                    {replacingSlide?.thumbnailUrl && (
                      <PolledThumbnailImage thumbnailUrl={replacingSlide.thumbnailUrl} themeId={selectedThemeId} />
                    )}
                  </StyledPreviewBox>
                </StyledPreviewColumn>
              </>
            )}
          </StyledPreviewRow>

          <StyledOptionCard $selected={selectedOption === ClassificationOption.NewSlide}>
            <StyledOption onClick={() => setSelectedOption(ClassificationOption.NewSlide)}>
              <input type="radio" checked={selectedOption === ClassificationOption.NewSlide} readOnly />
              <StyledOptionText>
                <StyledOptionTitle>New slide</StyledOptionTitle>
                <StyledOptionDescription>Add to the library as a brand-new asset</StyledOptionDescription>
              </StyledOptionText>
            </StyledOption>
          </StyledOptionCard>

          <StyledOptionCard $selected={selectedOption === ClassificationOption.VersionUpdate}>
            <StyledOption onClick={() => setSelectedOption(ClassificationOption.VersionUpdate)}>
              <input type="radio" checked={selectedOption === ClassificationOption.VersionUpdate} readOnly />
              <StyledOptionText>
                <StyledOptionTitle>Version update of existing slide</StyledOptionTitle>
                <StyledOptionDescription>This replaces an older version already in the library</StyledOptionDescription>
              </StyledOptionText>
            </StyledOption>

            {selectedOption === ClassificationOption.VersionUpdate && (
              <StyledReplacesSection>
                <StyledOptionDescription>Replaces:</StyledOptionDescription>
                <DropdownSelect
                  options={replacesOptions}
                  value={replacesOptions.find((option) => option.value === replacesSlideId) ?? null}
                  onChange={(option) => setReplacesSlideId(option?.value)}
                  isSearchable={false}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={REPLACES_SELECT_STYLES}
                />
              </StyledReplacesSection>
            )}
          </StyledOptionCard>

          <StyledOptionCard $selected={selectedOption === ClassificationOption.Duplicate}>
            <StyledOption onClick={() => setSelectedOption(ClassificationOption.Duplicate)}>
              <input type="radio" checked={selectedOption === ClassificationOption.Duplicate} readOnly />
              <StyledOptionText>
                <StyledOptionTitle>Set as duplicate</StyledOptionTitle>
                <StyledOptionDescription>Already exists in the library — skip adding this slide</StyledOptionDescription>
              </StyledOptionText>
            </StyledOption>

            {selectedOption === ClassificationOption.Duplicate && (
              <StyledReplacesSection>
                <StyledOptionDescription>Duplicate of:</StyledOptionDescription>
                <DropdownSelect
                  options={replacesOptions}
                  value={replacesOptions.find((option) => option.value === replacesSlideId) ?? null}
                  onChange={(option) => setReplacesSlideId(option?.value)}
                  isSearchable={false}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={REPLACES_SELECT_STYLES}
                />
              </StyledReplacesSection>
            )}
          </StyledOptionCard>
        </StyledContent>

        <StyledFooter>
          <NgButton variant={buttonVariants.clear} onClick={onClose}>
            Cancel
          </NgButton>
          <NgButton variant={buttonVariants.primary} onClick={handleSave}>
            Save
          </NgButton>
        </StyledFooter>
      </StyledDialogPaper>
    </Dialog>
  );
};
